import { Navigate } from "react-router-dom";
import type { ReactElement } from "react";
import type { RouteObject } from "react-router-dom";
import PlgRoute from "plg/shell/PlgRoute";
import PlgGuestRoute from "plg/shell/PlgGuestRoute";
import PlgSetupRoute from "plg/shell/PlgSetupRoute";
import { HIRE_ROUTES } from "plg/routes";
import { PLG_FEATURES } from "plg/features";
import PlgLayout from "plg/shell/PlgLayout";
import PlgHome from "plg/screens/Home/PlgHome";
import BusinessProfilePage from "plg/screens/Company/BusinessProfilePage";
import UnlockPage from "plg/screens/Unlocks/UnlockPage";
import FeedbackScreen from "components/Screens/Feedback/Feedback";
import TalentNetworkPage from "components/Screens/Marketplace/Customer/TalentNetwork/TalentNetworkPage";
import ProfilePage from "components/Screens/Marketplace/Customer/ProfilePage/ProfilePage";
import RequestAHirePage from "components/Screens/Marketplace/Customer/RequestAHire/RequestAHirePage";
import ShortlistPage from "components/Screens/Marketplace/Customer/Shortlist/ShortlistPage";
import InterviewStatusPage from "components/Screens/Marketplace/Customer/InterviewStatus/InterviewStatusPage";
import PlgSignInPage from "plg/screens/Signup/PlgSignInPage";
import PlgStepPage from "plg/screens/Signup/PlgStepPage";
import PlgWelcomePage from "plg/screens/Signup/PlgWelcomePage";
import GoogleOAuthCallbackPage from "plg/screens/Signup/GoogleOAuthCallbackPage";
import {
  LOGIN_ROUTE,
  PLG_SIGNUP_ROUTE,
  PLG_GOOGLE_CALLBACK_ROUTE,
  PLG_STEP_ROUTE,
  PLG_WELCOME_ROUTE,
} from "constants/routes";

const off = <Navigate to={LOGIN_ROUTE} replace />;
const guest = (page: ReactElement) => <PlgGuestRoute>{page}</PlgGuestRoute>;
const setup = (page: ReactElement) => <PlgSetupRoute>{page}</PlgSetupRoute>;

/**
 * Every PLG route. With the flag off each one redirects to the portal login. The sign-in page is signed-out
 * only; the setup steps and welcome are for a new sign-up or a signed-in customer whose setup is still open.
 */
export default function buildPlgRoutes(flag: boolean): RouteObject[] {
  return [
    { path: PLG_SIGNUP_ROUTE, element: flag ? guest(<PlgSignInPage />) : off },
    // The Google popup posts its result to the opener, so it is not guarded.
    { path: PLG_GOOGLE_CALLBACK_ROUTE, element: flag ? <GoogleOAuthCallbackPage /> : off },
    { path: PLG_WELCOME_ROUTE, element: flag ? setup(<PlgWelcomePage />) : off },
    { path: PLG_STEP_ROUTE, element: flag ? setup(<PlgStepPage />) : off },
    {
      path: "hire",
      element: (
        <PlgRoute>
          <PlgLayout />
        </PlgRoute>
      ),
      children: [
        { index: true, element: <Navigate to="home" replace /> },
        { path: "home", element: <PlgHome /> },
        // Hidden while the Company page is off: the catch-all below sends it to Home.
        ...(PLG_FEATURES.companyPage ? [{ path: "company", element: <BusinessProfilePage /> }] : []),
        { path: "talent-network", element: <TalentNetworkPage /> },
        { path: "talent-network/:id", element: <ProfilePage /> },
        { path: "shortlist", element: <ShortlistPage /> },
        { path: "request-a-hire", element: <RequestAHirePage /> },
        // Hidden while PLG interview requests are off: the catch-all below sends it to Home.
        ...(PLG_FEATURES.interviews ? [{ path: "interview-status", element: <InterviewStatusPage /> }] : []),
        { path: "unlocks/:feature", element: <UnlockPage /> },
        // The portal's Feedback screen, sending as the PLG customer.
        { path: "feedback", element: <FeedbackScreen plg /> },
        // Absolute: a relative "home" from a splat resolves against the unknown URL (/hire/x/home/home/…).
        { path: "*", element: <Navigate to={HIRE_ROUTES.home} replace /> },
      ],
    },
  ];
}
